import { call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import SessionApi from '../apis/session';
import { apiRequestStart, apiRequestEnd } from '../actions/loadingState/loadingStateActions';
import {
    getConversationDetailsSuccess,
    getConversationDetailsFailure,
    getSessionTimeSuccess,
    limitedLoginSuccess,
    loginFailure,
    loginSuccess,
    savePINSuccess,
    verifyDOBSuccess,
    verifyNameSuccess,
    resetForgottenPINSuccess,
    resetForgottenPINFailure,
} from '../actions/session/sessionActions';

import * as Sentry from "@sentry/browser";
import {conversationDetailsFromLocalStorage} from "../context";


// localStorage value controlling browser-level login/logout
const setLocalStorageToken = () => localStorage.setItem('token', Math.random().toString(36).substring(7));

/**
 * Set the response body passed to localStorage on successful getConversationDetails api call.
 * Persist this object so we can display Practice name and phone in the ui later.
 * @param response
 */
const setLocalStorageConversationDetails = response => localStorage.setItem('conversationDetails', JSON.stringify(response));

const nukeLocalStorage = () => localStorage.clear();

export function* resetForgottenPINSaga(request) {
    const { conversationHash } = request;
    const data = { conversationHash };

    const response = yield call(SessionApi.resetForgottenPIN, data);

    try {
        // start LoadingState
        yield put(apiRequestStart(request.type));

        /**
         * Chain the call to getConversationDetails() on success after resetForgottenPINSaga.
         * getConversationDetails will return hasConfirmedPIN: true,
         * that will tell the UI to display <Login />
         */
        const getConversationDetailsResponse = yield call(SessionApi.getConversationDetails, conversationHash);
        if (response && response.data.success) {
            const { conversationDetails } = getConversationDetailsResponse.data;
            yield put(getConversationDetailsSuccess(conversationDetails));
            yield call(SessionApi.logout);
        }
    }
    catch (error) {
        yield put(resetForgottenPINFailure(error));

        console.error('Error calling resetForgottenPIN!', error);
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(request.type));

        yield put(resetForgottenPINSuccess(response));
        yield put(push(`/verify/${conversationHash}`));
    }

}

export function* getConversationDetailsSaga(request) {
    try {
        const { conversationHash } = request;
        const response = yield call(SessionApi.getConversationDetails, conversationHash);

        if (response && response.data.success) {
            const { conversationDetails } = response.data;

            // create and yield a dispatch Effect
            yield put(getConversationDetailsSuccess(conversationDetails));

            setLocalStorageConversationDetails(conversationDetails);
        }
        else {
            yield put(getConversationDetailsFailure(response));
        }
    }
    catch (error) {
        console.error('Error calling getConversationDetails!', error);
        yield put(getConversationDetailsFailure(error));
    }
}

export function* getSessionTimeSaga() {
    try {
        const response = yield call(SessionApi.getSessionTime);

        const { success } = response.data;
        if (success) {
            yield put(getSessionTimeSuccess(response));
        }
    }
    catch (error) {
        console.error('Error calling getSessionTimeSaga!', error);
    }
}

export function* limitedLoginSaga(request) {
    try {
        // start LoadingState
        yield put(apiRequestStart(request.type));

        const { isExpired, conversationHash, PIN } = request;
        const data = {
            isExpired,
            conversationHash,
            PIN,
        };

        const response = yield call(SessionApi.login, data);

        if (response && response.data.success) {
            yield put(limitedLoginSuccess(response));
        }
    }
    catch (error) {
        yield put(loginFailure(error));
        console.error('Error calling login!', error);
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(request.type));
    }
}

export function* loginSaga(request) {
    try {
        // start LoadingState
        yield put(apiRequestStart(request.type));

        const { conversationHash, PIN } = request;
        const data = {
            conversationHash,
            PIN,
        };

        const response = yield call(SessionApi.login, data);

        const getConversationDetailsResponse = yield call(SessionApi.getConversationDetailsAfterLogin, conversationHash);
        if (response && response.data.success) {
            const { conversationDetails } = getConversationDetailsResponse.data;
            yield put(getConversationDetailsSuccess(conversationDetails));

            setLocalStorageConversationDetails(conversationDetails);
        }

        if (response && response.data.success) {
            setLocalStorageToken();

            // Start Sentry tracking of logged in Patient.
            if (Sentry.isInitialized() && response.config && response.data) {
                try {
                    const { patientId } = response.data;
                    const loginResponseInfo = JSON.parse(response.config.data);

                    if (loginResponseInfo && patientId) {
                        console.debug(`Sentry user with patientId, ${patientId}, is logged in to conversation with url hash: ${loginResponseInfo.conversationHash}`);
                        const details = conversationDetailsFromLocalStorage();
                        Sentry.setUser({
                            id: patientId,
                            username: loginResponseInfo.conversationHash,
                            practice_name: details ? details.practiceName : "unknown"
                        });
                    }
                }
                catch (error) {
                    console.error('Error parsing objects for Sentry!', error);
                    Sentry.captureMessage(
                        'Critical failure attempting to parse Patient objects during login.',
                        { level: 'error' }
                    )
                }
            }

            yield put(loginSuccess(response));
            yield put(push(`/chat/${conversationHash}`));
        }
    }
    catch (error) {
        yield put(loginFailure(error));
        console.error('Error calling login!', error);
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(request.type));
    }
}

export function* logoutSaga() {
    try {
        nukeLocalStorage();

        yield call(SessionApi.logout);
    }
    catch (error) {
        console.error('Error calling logout!', error);
    }
}

export function* savePINSaga(request) {

    const { conversationHash, PIN } = request;
    const data = { PIN };
    const response = yield call(SessionApi.savePIN, data);

    try {
        // start LoadingState
        yield put(apiRequestStart(request.type));

        /**
         * Chain the call to getConversationDetails() on success after savePIN.
         * getConversationDetails will return hasConfirmedPIN: true,
         * that will tell the UI to display <Login />
         */
        const getConversationDetailsResponse = yield call(SessionApi.getConversationDetails, conversationHash);
        if (response && response.data.success) {

            const { conversationDetails } = getConversationDetailsResponse.data;
            yield put(getConversationDetailsSuccess(conversationDetails));
            yield call(SessionApi.logout);

        }
    }
    catch (error) {
        // @TODO different failure
        yield put(loginFailure(error));

        console.error('Error calling savePIN!', error);
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(request.type));

        yield put(savePINSuccess(response));
        yield put(push(`/verify/${conversationHash}`));
    }

}

// Patients require validating their DOB and Last name to proceed with creating a PIN for the first time
export function* verifyDOBSaga(request) {
    try {
        // start LoadingState
        yield put(apiRequestStart(request.type));

        const { conversationHash, dateOfBirth, lastName } = request;
        const data = {
            conversationHash,
            dateOfBirth,
            lastName,
        };

        const response = yield call(SessionApi.verifyDOB, data);

        if (response && response.data.success) {
            yield put(verifyDOBSuccess(response.data.success));
        }
    }
    /**
     * All Promise failure from up the parent chain is caught and thrown here.
     */
    catch (error) {
        yield put(loginFailure(error));

        console.error('Error calling verifyDOB!', error);
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(request.type));
    }
}

// Contacts require validating their First and Last name to proceed with creating a PIN for the first time
export function* verifyNameSaga(request) {
    try {
        // start LoadingState
        yield put(apiRequestStart(request.type));

        const { conversationHash, firstName, lastName } = request;
        const data = {
            conversationHash,
            firstName,
            lastName,
        };

        const response = yield call(SessionApi.verifyName, data);

        if (response && response.data.success) {
            yield put(verifyNameSuccess(response.data.success));
        }
    }
    /**
     * All Promise failure from up the parent chain is caught and thrown here.
     */
    catch (error) {
        yield put(loginFailure(error));

        console.error('Error calling verifyName!', error);
    }
    finally {
        // end LoadingState
        yield put(apiRequestEnd(request.type));
    }
}
